import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import { useFormik } from "formik";
import * as Yup from "yup";
import { DataContext } from "../../context/dataprovider.js";
import usePaymentRequest from "../../components/paymentmethods.js";
import {
  handleFileChanges,
  handleRemoveFile,
} from "../../components/helperfunctions.js";

const CreditForm = ({ initialFormData, handleClose }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { userData } = useContext(DataContext);

  const { updatePayment, createPayment } = usePaymentRequest();

  useEffect(() => {
    if (initialFormData) {
      const existingUrls = Array.isArray(initialFormData.pr_attachments)
        ? initialFormData.pr_attachments.map((url) => ({
            url,
            name: url.split("/").pop(),
            isImage: /\.(jpg|jpeg|png|gif)$/i.test(url),
            source: "preloaded",
          }))
        : [];
      setSelectedFiles(existingUrls);
    }
  }, [initialFormData]);

  const initialValues = initialFormData || {
    project_id: "",
    amount: "",
    payment_type: "Credit",
    vendorname: "",
    accountnumber: "",
    receiving_date: null,
    remarks: "",
    other_remarks: "",
    pr_attachments: [],
    sender_name: userData?.name || "",
    sender_id: userData?.id || "",
    status: "Submitted",
    rejectedcause: "",
  };

  const validationSchema = Yup.object({
    project_id: Yup.string().required("Project is required"),
    amount: Yup.number().required("Amount is required"),
    payment_type: Yup.string().required("Payment Type is required"),
    vendorname: Yup.string().required("Vendor is required"),
    accountnumber: Yup.string().required("Account Number is required"),
    remarks: Yup.string().required("Remarks is required"),
    other_remarks: Yup.string().when("remarks", {
      is: "Other's",
      then: (schema) => schema.required("Remarks is required"),
      otherwise: (schema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    validate: (values) => {
      Object.keys(values).forEach((key) => {
        if (typeof values[key] === "string") {
          values[key] = values[key].trim();
        }
      });
      return {};
    },
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        if (file.source === "new") {
          formData.append("files", file.file);
        } else {
          formData.append("files", file.url);
        }
      });
      Object.keys(values).forEach((key) => {
        const value = values[key];
        formData.append(key, value || null);
      });

      if (!formData.has("sender_name")) {
        formData.append("sender_name", userData.name);
      }
      if (!formData.has("sender_id")) {
        formData.append("sender_id", userData.email);
      }
      if (!formData.has("payment_type")) {
        formData.append("payment_type", "Credit");
      }
      if (!formData.has("status")) {
        formData.append("status", "Submitted");
      }
      if (!formData.has("rejectedcause")) {
        formData.append("rejectedcause", "");
      }

      // Handle "Other's" remarks logic
      if (values.remarks !== "Other's") {
        formData.delete("other_remarks");
      } else {
        formData.set("remarks", values.other_remarks);
        formData.delete("other_remarks");
      }

      try {
        if (initialFormData && initialFormData.trans_id) {
          await updatePayment.mutateAsync({
            id: initialFormData.trans_id,
            data: formData,
          });
        } else {
          await createPayment.mutateAsync(formData);
        }
        resetForm();
        setSelectedFiles([]);
        handleClose();
      } catch (error) {
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        padding: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: "1.5rem", sm: "2rem" },
            marginBottom: { xs: 2, sm: 3 },
          }}
        >
          Credit Form
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Invoice Type</InputLabel>
          <Select
            required
            name="invoice_type"
            value={formik.values.invoice_type || ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={Boolean(initialFormData && initialFormData.trans_id)}
            error={
              formik.touched.invoice_type && Boolean(formik.errors.invoice_type)
            }
          >
            <MenuItem value="Proforma Invoice">Proforma Invoice</MenuItem>
            <MenuItem value="Tax Invoice">Tax Invoice</MenuItem>
          </Select>
          {formik.touched.invoice_type && formik.errors.invoice_type && (
            <FormHelperText error>{formik.errors.invoice_type}</FormHelperText>
          )}
        </FormControl>
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            mt: { xs: 2, sm: 3 },
            "& .MuiFormControl-root": { m: 0 },
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  name="project_id"
                  required
                  value={formik.values.project_id || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.project_id &&
                    Boolean(formik.errors.project_id)
                  }
                >
                  {formik.values.project_id &&
                    formik.values.project_id !== "" &&
                    (!userData?.projectId ||
                      !userData.projectId.includes(
                        formik.values.project_id
                      )) && (
                      <MenuItem value={formik.values.project_id}>
                        {formik.values.project_id}
                      </MenuItem>
                    )}
                  {userData?.projectId?.map((project, index) => (
                    <MenuItem key={index} value={project}>
                      {project}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.project_id && formik.errors.project_id && (
                  <FormHelperText error>
                    {formik.errors.project_id}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="amount"
                label="Amount"
                required
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <Select
                  name="payment_type"
                  value={formik.values.payment_type || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.payment_type &&
                    Boolean(formik.errors.payment_type)
                  }
                >
                  <MenuItem value="Credit">Credit</MenuItem>
                  <MenuItem value="Refund">Refund</MenuItem>
                  <MenuItem value="Advance">Advance</MenuItem>
                </Select>
                {formik.touched.payment_type && formik.errors.payment_type && (
                  <FormHelperText error>
                    {formik.errors.payment_type}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="vendorname"
                label="Vendor"
                required
                value={formik.values.vendorname}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.vendorname && Boolean(formik.errors.vendorname)
                }
                helperText={
                  formik.touched.vendorname && formik.errors.vendorname
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="accountnumber"
                label="Account Number"
                required
                value={formik.values.accountnumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.accountnumber &&
                  Boolean(formik.errors.accountnumber)
                }
                helperText={
                  formik.touched.accountnumber && formik.errors.accountnumber
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="receiving_date"
                label="Receiving Date"
                type="date"
                value={
                  formik.values.receiving_date
                    ? formik.values.receiving_date.slice(0, 10)
                    : ""
                }
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Remarks dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Remarks</InputLabel>
                <Select
                  required
                  name="remarks"
                  value={formik.values.remarks || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.remarks && Boolean(formik.errors.remarks)
                  }
                >
                  {formik.values.remarks &&
                    formik.values.remarks !== "" &&
                    !userData?.remarks?.includes(formik.values.remarks) && (
                      <MenuItem value={formik.values.remarks}>
                        {formik.values.remarks}
                      </MenuItem>
                    )}
                  {userData?.remarks?.map((reason, index) => (
                    <MenuItem key={index} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                  <MenuItem value="Other's">Other's</MenuItem>
                </Select>
                {formik.touched.remarks && formik.errors.remarks && (
                  <FormHelperText error>{formik.errors.remarks}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {formik.values.remarks === "Other's" && (
              <Grid item xs={12}>
                <TextField
                  name="other_remarks"
                  label="Enter Remarks"
                  value={formik.values.other_remarks}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.other_remarks &&
                    Boolean(formik.errors.other_remarks)
                  }
                  helperText={
                    formik.touched.other_remarks && formik.errors.other_remarks
                  }
                  fullWidth
                />
              </Grid>
            )}

            {Array.isArray(selectedFiles) && selectedFiles.length > 0 && (
              <Box mt={2}>
                <Typography variant="h6">Selected Files:</Typography>
                <Box display="flex" flexWrap="wrap">
                  {selectedFiles.map((item, index) => {
                    const previewUrl =
                      item.source === "new" && item.file
                        ? URL.createObjectURL(item.file)
                        : item.url;

                    return (
                      <Box
                        position="relative"
                        key={index}
                        marginRight="10px"
                        marginBottom="10px"
                      >
                        {item.isImage ? (
                          <img
                            src={previewUrl}
                            alt={item.name}
                            style={{
                              width: "100px",
                              height: "100px",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              width: "100px",
                              height: "100px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              border: "1px dashed gray",
                              textDecoration: "none",
                              color: "black",
                            }}
                          >
                            <DescriptionIcon
                              style={{ fontSize: "40px", color: "gray" }}
                            />
                            <span
                              style={{ marginTop: "5px", fontSize: "14px" }}
                            >
                              View Document
                            </span>
                          </a>
                        )}
                        <IconButton
                          onClick={() =>
                            handleRemoveFile(setSelectedFiles, item.name)
                          }
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            color: "red",
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Grid item xs={12}>
              <TextField
                type="file"
                name="pr_attachments"
                label="Pick Files"
                onChange={(event) =>
                  handleFileChanges(event, selectedFiles, setSelectedFiles)
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ multiple: true }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    paddingY: 1,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                disabled={formik.isSubmitting || !formik.isValid}
                sx={{
                  mt: { xs: 2, sm: 3 },
                  py: { xs: 1.5, sm: 2 },
                }}
              >
                {formik.isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default CreditForm;
